import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  message: string;
};

/**
 * Top-level error boundary. Wraps <RouterProvider /> so a crash in any route
 * tree (including Suspense fallbacks for lazy pages) surfaces a recoverable
 * message instead of a blank white page.
 *
 * Recovery options:
 *  - "刷新页面"   — full reload, the safest reset for client-state corruption.
 *  - "回到首页"   — navigates to "/" via window.location; bypasses the router
 *                  which may itself be the crash source.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : "未知错误",
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // No external error sink in MVP — log to console so dev tools surface it.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 32px",
          background: "var(--color-bg, var(--color-bg))",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: "#8a6e4f",
            marginBottom: 12,
          }}
        >
          Something Went Wrong
        </div>
        <div
          style={{
            fontSize: 72,
            fontWeight: 300,
            color: "var(--color-wine)",
            letterSpacing: -2,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          !
        </div>
        <div
          style={{
            width: 32,
            height: 1,
            background: "var(--color-gold)",
            margin: "20px 0",
          }}
        />
        <div
          style={{
            fontSize: 16,
            color: "#1a1a1a",
            letterSpacing: 0.4,
            marginBottom: 6,
          }}
        >
          出错了
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#8a8580",
            letterSpacing: 0.3,
            maxWidth: 280,
            lineHeight: 1.6,
            marginBottom: 8,
          }}
        >
          页面渲染异常，请刷新或返回首页。
        </div>
        {this.state.message && (
          <div
            style={{
              fontSize: 11,
              color: "#a83d4a",
              letterSpacing: 0.2,
              maxWidth: 280,
              lineHeight: 1.5,
              marginBottom: 24,
              wordBreak: "break-word",
            }}
          >
            {this.state.message}
          </div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 18px",
              border: "1px solid #d1c9bf",
              borderRadius: 999,
              background: "transparent",
              color: "#4a4a4a",
              fontSize: 13,
              letterSpacing: 0.4,
              cursor: "pointer",
            }}
          >
            刷新页面
          </button>
          <button
            type="button"
            onClick={this.handleGoHome}
            style={{
              padding: "10px 18px",
              border: "1px solid var(--color-wine)",
              borderRadius: 999,
              background: "var(--color-wine)",
              color: "#fff",
              fontSize: 13,
              letterSpacing: 0.4,
              cursor: "pointer",
            }}
          >
            回到首页
          </button>
        </div>
      </div>
    );
  }
}
